// The route planner, run in a Web Worker (planner.worker.js).
//
// Why: dispatchPlan creates millions of short-lived objects -- every candidate
// position of every stop is a simulated day. On the page's own thread, in a
// page holding ~120 MB, the browser's garbage collection made that 20-30x
// slower than the same code under node: ย้ายไปรถ took 11-20 seconds on a
// 16-stop day, and วางแผน froze the page while it planned. Measured in the
// browser on 2026-09-29: the same allocation-heavy loop ran 439 ms on the page
// and 83 ms in a worker. A worker has its own small heap, and the page keeps
// drawing -- its progress bar included -- while the planner works.
//
// Everything crosses by structured clone, so results arrive as plain copies:
// the caller freezes what it keeps (see dispatchPlan.vue).
//
// If a worker cannot be started, or dies, the same call runs on the page
// instead -- slower, but a plan is never refused because of where it ran.
const P = require('./dispatchPlan')
const geo = require('./geo')
const UP = require('./upcountryPlan')

// The fallback plans on this thread, so it needs the wide coordinates too --
// fetched as their own chunk, only by a browser whose worker would not start.
// A chunk that will not load costs the out-of-area routes their points, never
// the Bangkok plan: the call goes ahead without it, and the next one tries again.
let wide = null
function loadWide () {
  if (!wide) {
    wide = import(/* webpackChunkName: "geo-wide" */ '../geoPointsWide.json')
      .then(m => geo.addPoints(m.default || m))
      .catch(() => { wide = null })
  }
  return wide
}

let worker = null
let broken = false
let seq = 0
const pending = {}

function start () {
  if (worker || broken) return worker
  try {
    const PlannerWorker = require('./planner.worker.js')
    worker = new PlannerWorker()
  } catch (e) {
    broken = true
    return null
  }
  worker.onmessage = event => {
    const msg = event.data || {}
    const job = pending[msg.id]
    if (!job) return
    if (msg.progress) { if (job.onProgress) job.onProgress(msg.progress); return }
    delete pending[msg.id]
    if (msg.error) job.reject(new Error(msg.error))
    else job.resolve(msg.result)
  }
  worker.onerror = () => {
    // A dead worker: finish what was waiting on it here, and stop using it.
    broken = true
    if (worker) worker.terminate()
    worker = null
    Object.keys(pending).forEach(id => {
      const job = pending[id]
      delete pending[id]
      runHere(job.fn, job.args, job.onProgress).then(job.resolve, job.reject)
    })
  }
  return worker
}

function runHere (fn, args, onProgress) {
  return loadWide().then(() => {
    if (fn === 'planOptions') return P.planOptions(args[0], onProgress)
    if (fn === 'moveMenu') return P.moveChoices(args[0], args[1])
    if (fn === 'moveLocal') return P.moveLocal(args[0], args[1], args[2])
    if (fn === 'tripForCar') return P.tripForCar(args[0], args[1], args[2])
    if (fn === 'retimeTripSplit') return P.retimeTripSplit(args[0], args[1], args[2])
    if (fn === 'planUpTrip') return UP.planTrip(args[0])
    if (fn === 'retimeUpTrip') return UP.retime(args[0])
    if (fn === 'refitUpTrip') return UP.refitPlan(args[0])
    throw new Error('unknown planner call: ' + fn)
  })
}

function call (fn, args, onProgress) {
  const w = start()
  if (!w) return runHere(fn, args, onProgress)
  return new Promise((resolve, reject) => {
    const id = ++seq
    pending[id] = { fn: fn, args: args, onProgress: onProgress, resolve: resolve, reject: reject }
    try {
      w.postMessage({ id: id, fn: fn, args: args })
    } catch (e) {
      // Something in the input would not clone; plan it here instead.
      delete pending[id]
      runHere(fn, args, onProgress).then(resolve, reject)
    }
  })
}

// Both plans for a day (dispatchPlan.planOptions), reporting after each run:
// onProgress({ done, total }).
function planOptions (input, onProgress) {
  return call('planOptions', [input], onProgress)
}

// ย้ายไปรถ's menu for one stop of `plan` (dispatchPlan.moveChoices).
function moveMenu (plan, jobId) {
  return call('moveMenu', [plan, jobId])
}

// One stop of `plan` moved onto `regNum` (dispatchPlan.moveLocal).
function moveLocal (plan, jobId, regNum) {
  return call('moveLocal', [plan, jobId, regNum])
}

// One out-of-area route timed on one named car (dispatchPlan.tripForCar).
function tripForCar (plan, tripId, regNum) {
  return call('tripForCar', [plan, tripId, regNum])
}

// A two-day route with its day boundary moved (dispatchPlan.retimeTripSplit).
function retimeTripSplit (plan, trip, split) {
  return call('retimeTripSplit', [plan, trip, split])
}

// An up-country trip's route plan (upcountryPlan.planTrip).
function planUpTrip (input) {
  return call('planUpTrip', [input])
}

// The same trip re-timed in the order staff set (upcountryPlan.retime).
function retimeUpTrip (input) {
  return call('retimeUpTrip', [input])
}

// A saved plan fitted to the trip's orders now (upcountryPlan.refitPlan).
function refitUpTrip (input) {
  return call('refitUpTrip', [input])
}

module.exports = { planOptions, moveMenu, moveLocal, tripForCar, retimeTripSplit, planUpTrip, retimeUpTrip, refitUpTrip }
